import { z } from "zod";

// ============================================================================
// ALERT ACTION ENUMS
// ============================================================================

export const ALERT_ACTION_CHANNELS = ["email", "sms", "phone", "notification", "webhook", "emergency"] as const;
export type AlertActionChannel = (typeof ALERT_ACTION_CHANNELS)[number];

export const ALERT_ACTION_STATUSES = ["pending", "sent", "delivered", "failed", "acknowledged", "resolved"] as const;
export type AlertActionStatus = (typeof ALERT_ACTION_STATUSES)[number];

// ============================================================================
// BASE TRIGGERED ALERT ACTION SCHEMA
// ============================================================================

const BaseTriggeredAlertActionSchema = z.object({
  // Primary fields
  id: z.string(),

  // Relations
  triggeredAlertId: z.string(),

  // Action details
  channel: z.enum(ALERT_ACTION_CHANNELS),
  status: z.enum(ALERT_ACTION_STATUSES),

  // Recipients (array of user IDs, phone numbers, emails, etc.)
  recipients: z.array(z.string()),

  // Delivery tracking
  sentAt: z.string().nullable().optional(),
  deliveredAt: z.string().nullable().optional(),
  failedAt: z.string().nullable().optional(),
  failureReason: z.string().nullable().optional(),

  // Acknowledgment & Resolution
  acknowledgedBy: z.string().nullable().optional(),
  acknowledgedAt: z.string().nullable().optional(),
  resolvedBy: z.string().nullable().optional(),
  resolvedAt: z.string().nullable().optional(),
  resolutionNotes: z.string().nullable().optional(),

  // Metadata (channel-specific details)
  metadata: z.record(z.string(), z.unknown()),

  createdAt: z.string(),
  updatedAt: z.string(),
});

export const TriggeredAlertActionSchema = BaseTriggeredAlertActionSchema;

export const TriggeredAlertActionListItemSchema = BaseTriggeredAlertActionSchema;

export const TriggeredAlertActionListSchema = z.array(TriggeredAlertActionListItemSchema);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type TriggeredAlertAction = z.infer<typeof TriggeredAlertActionSchema>;
export type TriggeredAlertActionListItem = z.infer<typeof TriggeredAlertActionListItemSchema>;
